// src/pages/customer/customerpanel/CartPanel.jsx
import React, { useState, useEffect } from 'react';
import api from '../../../utils/api';
import { useCart } from '../useCart';
import { useAuth } from '../../../context/AuthContext';

const SHOW_ONLINE_PAYMENT = false;

// ── Step 1: Cart Review ────────────────────────────────────
function CartReview({ cart, total, feeAmount, grandTotal, removeFromCart, onProceed }) {
  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <div>
        <h2 className="text-xl font-black text-on-surface">My Cart</h2>
        <p className="text-sm text-secondary mt-1">
          {cart.length} animal{cart.length !== 1 ? 's' : ''} selected
        </p>
      </div>

      {cart.length === 0 ? (
        <div className="bg-white rounded-2xl p-14 shadow-sm border border-outline-variant/10
                        flex flex-col items-center gap-3 text-center">
          <span className="material-symbols-outlined text-[48px] text-outline">
            shopping_cart
          </span>
          <p className="text-lg font-black">Your cart is empty</p>
          <p className="text-sm text-secondary">Browse animals and add them to your cart.</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {cart.map(item => (
              <div key={item.id}
                className="bg-white rounded-2xl p-4 shadow-sm
                           border border-outline-variant/10 flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-surface-container
                                flex-shrink-0 flex items-center justify-center">
                  {item.image
                    ? <img src={item.image} alt={item.name}
                           className="w-full h-full object-cover" />
                    : <span className="material-symbols-outlined text-[32px] text-outline">
                        pets
                      </span>
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-black text-sm text-on-surface">{item.name}</p>
                  <p className="text-xs text-secondary">{item.category}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-black text-primary text-sm whitespace-nowrap">
                    ₹{(Number(item.price) * Number(item.quantity || 1)).toLocaleString('en-IN')}
                  </p>
                  {Number(item.quantity) > 1 && (
                    <p className="text-[10px] text-secondary">
                      ₹{Number(item.price).toLocaleString('en-IN')} × {item.quantity}
                    </p>
                  )}
                </div>
                <button onClick={() => removeFromCart(item.id)}
                  className="w-8 h-8 rounded-full hover:bg-red-50 text-secondary
                             hover:text-red-500 flex items-center justify-center transition-all">
                  <span className="material-symbols-outlined text-[18px]">delete</span>
                </button>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-outline-variant/10
                          space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-secondary">Subtotal ({cart.length} items)</span>
              <span className="font-bold">₹{total.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-secondary">Platform Fee</span>
              <span className={`font-bold ${feeAmount === 0 ? 'text-emerald-600' : 'text-on-surface'}`}>
                {feeAmount === 0 ? 'Free' : `₹${feeAmount.toLocaleString('en-IN')}`}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-secondary">Delivery</span>
              <span className="font-bold text-secondary">Arranged by Farmer</span>
            </div>
            <div className="border-t border-surface-container pt-3 flex justify-between">
              <span className="font-black text-on-surface">Total</span>
              <span className="font-black text-primary text-lg">
                ₹{grandTotal.toLocaleString('en-IN')}
              </span>
            </div>
            <button onClick={onProceed}
              className="w-full py-3 rounded-xl bg-primary text-white font-bold
                         hover:opacity-90 transition-all flex items-center
                         justify-center gap-2">
              <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
              Proceed to Checkout
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const Field = ({ label, placeholder, type = 'text', value, onChange, validate }) => {
  const [touched, setTouched] = useState(false);
  const error = touched && validate ? validate(value) : '';

  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-bold text-secondary uppercase tracking-wider">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        onBlur={() => setTouched(true)}
        placeholder={placeholder}
        className={`w-full px-4 py-2.5 rounded-xl border text-sm outline-none
                   transition-all placeholder:text-outline-variant bg-surface-container-low
                   ${error
                     ? 'border-red-400 focus:ring-2 focus:ring-red-200'
                     : 'border-outline-variant/30 focus:ring-2 focus:ring-primary/20 focus:border-primary/40'
                   }`}
      />
      {error && (
        <p className="text-[11px] text-red-500 flex items-center gap-1">
          <span className="material-symbols-outlined text-[13px]">error</span>
          {error}
        </p>
      )}
    </div>
  );
};

// ── Step 2: Checkout Form ──────────────────────────────────
function CheckoutForm({ cart, total, feeAmount, grandTotal, user, onBack, onSuccess , apiBase }) {
  const [form, setForm] = useState({
    name:    user?.name    || '',
    phone:   user?.phone   || '',
    address: user?.address || '',
    city:    user?.city    || '',
    state:   user?.state   || '',
    pincode: user?.pincode || '',
    note:    '',
  });
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [placing, setPlacing] = useState(false);
  const [error,   setError]   = useState('');

  const set = (field) => (e) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async () => {
    const nameVal    = form.name.trim();
    const phoneVal   = form.phone.trim();
    const addressVal = form.address.trim();
    const cityVal    = form.city.trim();
    const pincodeVal = form.pincode.trim();

    if (nameVal.length < 2) {
      setError('Please enter a valid full name.'); return;
    }
    if (!/^[a-zA-Z\s'-]{2,}$/.test(nameVal)) {
      setError('Name should contain only letters.'); return;
    }
    if (!/^[6-9]\d{9}$/.test(phoneVal)) {
      setError('Enter a valid 10-digit mobile number starting with 6–9.'); return;
    }
    if (addressVal.length < 5) {
      setError('Please enter a complete address.'); return;
    }
    if (!/^[a-zA-Z\s]{2,}$/.test(cityVal)) {
      setError('City should contain only letters, no numbers.'); return;
    }
    if (pincodeVal && !/^\d{6}$/.test(pincodeVal)) {
      setError('Pincode must be exactly 6 digits.'); return;
    }
    setPlacing(true); setError('');
    try {
      const placedOrders = [];

      for (const item of cart) {
        const isMeat = String(item.id).startsWith('meat_');

        if (isMeat) {
          const realId = String(item.id).replace('meat_', '');
          const { data } = await api.post('/api/customer/shop-orders', {
            product_id:       realId,
            amount: Number(item.price) * Number(item.quantity || 1),
            quantity:         item.quantity || 1,
            payment_method:   paymentMethod,
            delivery_name:    form.name,
            delivery_phone:   form.phone,
            delivery_address: `${form.address}, ${form.city}, ${form.state} ${form.pincode}`.trim(),
            note:             form.note,
          }
        );
          placedOrders.push({ ...data, amount: Number(item.price) * Number(item.quantity || 1), animal_name: item.name, _isShop: true });

        } else {
          const { data } = await api.post(apiBase, {
            animal_id:        item.id,
            amount:           Number(item.price) * Number(item.quantity || 1),
            quantity:         item.quantity || 1,
            payment_method:   paymentMethod,
            delivery_name:    form.name,
            delivery_phone:   form.phone,
            delivery_address: `${form.address}, ${form.city}, ${form.state} ${form.pincode}`.trim(),
            note:             form.note,
          }
        );
          placedOrders.push({ ...data, amount: Number(item.price) * Number(item.quantity || 1), animal_name: item.name, _isShop: false });
        }
      }

      if (paymentMethod === 'online') {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        document.body.appendChild(script);
        await new Promise(r => script.onload = r);

        for (const order of placedOrders) {
          const isShop = order._isShop; 
          const { data: rzpData } = await api.post(
            isShop ? '/api/payment/create-shop-order' : '/api/payment/create-order',
            { order_id: order.id }
          );
          await new Promise((resolve, reject) => {
            const rzp = new window.Razorpay({
              key:         rzpData.key_id,
              amount:      rzpData.amount,
              currency:    rzpData.currency,
              name:        'Elite Goats',
              description: order.animal_name,
              order_id:    rzpData.razorpay_order_id,
              prefill:     { name: form.name, contact: form.phone },
              theme:       { color: '#1a6b3c' },
              handler: async (response) => {
                await api.post(
                  isShop ? '/api/payment/verify-shop' : '/api/payment/verify',
                  {
                    razorpay_order_id:   response.razorpay_order_id,
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature:  response.razorpay_signature,
                    order_id:            order.id,
                  }
                );
                resolve();
              },
              modal: { ondismiss: () => reject(new Error('Payment cancelled')) },
            });
            rzp.open();
          });
        }
      }

      onSuccess();
    } catch (err) {
      console.error('Order error:', err.response?.data || err.message);
      setError(err.message === 'Payment cancelled'
        ? 'Payment was cancelled. Your order is saved — you can pay later from My Orders.'
        : err.response?.data?.error || 'Could not place order. Please try again.');
    } finally { setPlacing(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5">

      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack}
          className="w-9 h-9 rounded-full hover:bg-surface-container flex items-center
                     justify-center transition-all text-secondary">
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
        </button>
        <div>
          <h2 className="text-xl font-black text-on-surface">Checkout</h2>
          <p className="text-sm text-secondary mt-0.5">Enter your delivery details</p>
        </div>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center
                          justify-center text-[10px] font-black">✓</div>
          <span className="text-xs font-bold text-secondary">Cart Review</span>
        </div>
        <div className="flex-1 h-px bg-primary/30" />
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center
                          justify-center text-[10px] font-black">2</div>
          <span className="text-xs font-bold text-primary">Checkout</span>
        </div>
        <div className="flex-1 h-px bg-outline-variant/20" />
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-6 rounded-full bg-surface-container text-secondary
                          flex items-center justify-center text-[10px] font-black">3</div>
          <span className="text-xs font-bold text-secondary">Confirmed</span>
        </div>
      </div>

      {/* Delivery form */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-outline-variant/10
                      space-y-4">
        <h3 className="font-black text-sm text-on-surface">Delivery Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full Name *" placeholder="Your full name"
            value={form.name} onChange={set('name')}
            validate={v => !/^[a-zA-Z\s'-]{2,}$/.test(v.trim()) ? 'Letters only, min 2 characters' : ''} />
          <Field label="Phone *" placeholder="10-digit mobile" type="tel"
            value={form.phone} onChange={set('phone')}
            validate={v => !/^[6-9]\d{9}$/.test(v.trim()) ? 'Valid 10-digit number required' : ''} />
        </div>
        <Field label="Address *" placeholder="House no., Street, Area"
          value={form.address} onChange={set('address')}
          validate={v => v.trim().length < 5 ? 'Enter a complete address' : ''} />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Field label="City *" placeholder="City"
            value={form.city} onChange={set('city')}
            validate={v => !/^[a-zA-Z\s]{2,}$/.test(v.trim()) ? 'City name, letters only' : ''} />
          <Field label="State" placeholder="State"
            value={form.state} onChange={set('state')}
            validate={v => v && !/^[a-zA-Z\s]{2,}$/.test(v.trim()) ? 'Letters only' : ''} />
          <Field label="Pincode" placeholder="6-digit"
            value={form.pincode} onChange={set('pincode')}
            validate={v => v && !/^\d{6}$/.test(v.trim()) ? 'Must be exactly 6 digits' : ''} />
        </div>
        <div className="space-y-1.5">
          <label className="text-[11px] font-bold text-secondary uppercase tracking-wider">
            Note to Farmer (optional)
          </label>
          <textarea
            value={form.note}
            onChange={set('note')}
            rows={3}
            placeholder="Any special instructions…"
            className="w-full px-4 py-2.5 rounded-xl border border-outline-variant/30
                       bg-surface-container-low text-sm outline-none resize-none
                       focus:ring-2 focus:ring-primary/20 focus:border-primary/40
                       transition-all placeholder:text-outline-variant"
          />
        </div>
      </div>

      {/* Order summary */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-outline-variant/10
                      space-y-3">
        <h3 className="font-black text-sm text-on-surface">Order Summary</h3>
        {cart.map(item => (
          <div key={item.id} className="flex justify-between text-sm">
            <span className="text-on-surface">{item.name}
              <span className="text-secondary text-xs ml-1">({item.category})</span>
            </span>
            <span className="font-bold text-primary">
              ₹{(Number(item.price) * Number(item.quantity || 1)).toLocaleString('en-IN')}
              {Number(item.quantity) > 1 && (
                <span className="text-[10px] text-secondary font-normal ml-1">
                  (×{item.quantity})
                </span>
              )}
            </span>
          </div>
        ))}
        <div className="flex justify-between text-sm">
          <span className="text-secondary">Platform Fee</span>
          <span className={`font-bold ${feeAmount === 0 ? 'text-emerald-600' : 'text-on-surface'}`}>
            {feeAmount === 0 ? 'Free' : `₹${feeAmount.toLocaleString('en-IN')}`}
          </span>
        </div>
        <div className="border-t border-surface-container pt-3 flex justify-between">
          <span className="font-black text-on-surface">Total Payable</span>
          <span className="font-black text-primary text-lg">
            ₹{grandTotal.toLocaleString('en-IN')}
          </span>
        </div>

        {/* Payment Method Selector */}
        <div className="space-y-2">
          <label className="text-[11px] font-bold text-secondary uppercase tracking-wider">
            Payment Method
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => setPaymentMethod('cod')}
              className={`p-3 rounded-xl border-2 text-left transition-all ${
                paymentMethod === 'cod'
                  ? 'border-primary bg-primary/5'
                  : 'border-outline-variant/30 hover:border-primary/30'
              }`}>
              <span className="material-symbols-outlined text-[20px] text-amber-600">payments</span>
              <p className="text-sm font-bold mt-1">Cash on Delivery</p>
              <p className="text-[11px] text-secondary">Pay when animal is delivered</p>
            </button>
              {SHOW_ONLINE_PAYMENT && (
                <button onClick={() => setPaymentMethod('online')}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    paymentMethod === 'online'
                      ? 'border-primary bg-primary/5'
                      : 'border-outline-variant/30 hover:border-primary/30'
                  }`}>
                  <span className="material-symbols-outlined text-[20px] text-blue-600">credit_card</span>
                  <p className="text-sm font-bold mt-1">Pay Online</p>
                  <p className="text-[11px] text-secondary">UPI, Card, Net Banking</p>
                </button>
              )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3
                          rounded-xl text-sm flex items-center gap-2">
            <span className="material-symbols-outlined text-base">error</span>
            {error}
          </div>
        )}

        <button onClick={handleSubmit} disabled={placing}
          className="w-full py-3 rounded-xl bg-primary text-white font-bold text-sm
                     hover:opacity-90 disabled:opacity-50 transition-all
                     flex items-center justify-center gap-2">
          {/* {placing
            ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing…</>
            : paymentMethod === 'online'
              ? <><span className="material-symbols-outlined text-[18px]">credit_card</span> Place Order & Pay Online</>
              : <><span className="material-symbols-outlined text-[18px]">check_circle</span> Confirm & Place Order (COD)</>
          } */}
          {placing
            ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing…</>
            : <><span className="material-symbols-outlined text-[18px]">check_circle</span> Confirm & Place Order (COD)</>
          }
        </button>
      </div>
    </div>
  );
}

// ── Step 3: Success ────────────────────────────────────────
function OrderSuccess({ onDone }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
      <span className="material-symbols-outlined text-[64px] text-emerald-500">
        check_circle
      </span>
      <h2 className="text-2xl font-black text-on-surface">Order Placed!</h2>
      <p className="text-secondary text-sm max-w-xs">
        Your order is confirmed. The farmer will contact you shortly to arrange delivery.
      </p>
      <button onClick={onDone}
        className="mt-4 px-6 py-2.5 rounded-full bg-primary text-white font-bold
                   text-sm hover:opacity-90 transition-all">
        View My Orders
      </button>
    </div>
  );
}

// ── Main export ────────────────────────────────────────────
export default function CartPanel({ onNavigate, apiBase = '/api/customer/orders', user: userProp }) {
  const { user: authUser } = useAuth();
  const user = userProp || authUser;
  const { cart, removeFromCart, clearCart } = useCart(user);
  const [step, setStep] = useState('review'); // 'review' | 'checkout' | 'success'
  const [platformFee, setPlatformFee] = useState({ type: 'free', amount: 0 });

  useEffect(() => {
    api.get('/api/public/settings')
      .then(({ data }) => {
        if (data.fee_type) {
          setPlatformFee({ type: data.fee_type, amount: Number(data.platform_fee || 0) });
        }
      })
      .catch(() => {});
  }, []);

  const total = cart.reduce((sum, i) => sum + Number(i.price) * Number(i.quantity || 1), 0);

  const feeAmount =
    platformFee.type === 'free'      ? 0
    : platformFee.type === 'percent' ? Math.round(total * platformFee.amount / 100)
    : platformFee.amount;

  const grandTotal = total + feeAmount;

  const handleSuccess = () => {
    clearCart();
    setStep('success');
  };

  if (step === 'success') {
    return <OrderSuccess onDone={() => onNavigate?.('orders')} />;
  }

  if (step === 'checkout') {
    return (
      <CheckoutForm
        cart={cart}
        total={total}
        feeAmount={feeAmount}
        grandTotal={grandTotal}
        user={user}
        onBack={() => setStep('review')}
        onSuccess={handleSuccess}
        apiBase={apiBase}  
      />
    );
  }

  return (
    <CartReview
      cart={cart}
      total={total}
      feeAmount={feeAmount}
      grandTotal={grandTotal}
      removeFromCart={removeFromCart}
      onProceed={() => setStep('checkout')}
      apiBase={apiBase}     
    />
  );
}
